import { gql, useQuery } from "@apollo/client";
import { Menu, Popover, Transition } from "@headlessui/react";
import {
  BellIcon,
  ChevronDownIcon,
  MenuIcon,
  XIcon
} from "@heroicons/react/outline";
import cn from "classnames";
import React, { Fragment } from "react";
import { useDispatch } from "react-redux";
import { matchPath } from "react-router";
import { Link, NavLink, useLocation } from "react-router-dom";
import Logo from "../../../components/Logo";
import {
  ROUTE_HOME,
  ROUTE_SETTINGS
} from "../../../routes";
import { logout } from "../../../store/modules/user/actions";

const navigation = [
  { name: "Home", to: ROUTE_HOME },
  // { name: "Accounts", to: ROUTE_ACCOUNTS },
  // { name: "Transactions", to: ROUTE_TRANSACTIONS },
  { name: "Settings", to: ROUTE_SETTINGS },
];

const userNavigation = [
  { name: "Settings", to: ROUTE_SETTINGS },
  { name: "Sign out", signOut: true },
];

const Header = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { data } = useQuery(gql`
    {
      self {
        email
        fullName
      }
    }
  `);

  return (
    <Popover
      as="header"
      className="pb-24 bg-gradient-to-r from-teal-800 to-green-600"
    >
      {({ open }) => (
        <>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8">
            <div className="relative flex flex-wrap items-center justify-center lg:justify-between lg:py-0 py-2">
              <div className="absolute left-0 py-3 flex-shrink-0 lg:static flex items-center">
                <a href="#s">
                  <span className="sr-only">Due</span>
                  <Logo
                    className="fill-current text-green-200"
                    type="plain-color"
                    size={30}
                  />
                </a>
              </div>

              {/* Right section */}
              <div className="hidden lg:ml-4 lg:flex lg:items-center lg:py-3 lg:pr-0.5">
                <button
                  type="button"
                  className={cn(
                    "flex-shrink-0 flex items-center p-0.5",
                    "transition-all hover:bg-green-200 bg-opacity-10 hover:bg-opacity-20 rounded-full",
                    "text-white text-sm font-medium",
                    "outline-none focus:outline-none focus:ring ring-green-200 ring-opacity-40"
                  )}
                >
                  <span className="sr-only">View notifications</span>
                  <BellIcon className="h-6 w-6" aria-hidden="true" />
                </button>

                {/* Profile dropdown */}
                {data && (
                  <Menu as="div" className="ml-4 relative flex-shrink-0">
                    {({ open }) => (
                      <>
                        <Transition
                          appear={true}
                          show={true}
                          enter="transition-opacity duration-75"
                          enterFrom="opacity-0"
                          enterTo="opacity-100"
                          leave="transition-opacity duration-150"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Menu.Button
                            className={cn(
                              "flex-shrink-0 flex items-center px-4 py-1",
                              "transition-all bg-green-200 bg-opacity-10 hover:bg-opacity-20 rounded-full",
                              "text-white text-sm font-medium",
                              "outline-none focus:outline-none focus:ring ring-green-200 ring-opacity-40"
                            )}
                          >
                            <span className="sr-only">Open user menu</span>
                            <div className="flex-shrink-0">
                              {data.self.fullName}
                            </div>
                            <ChevronDownIcon className="w-4 h-4 ml-2" />
                          </Menu.Button>{" "}
                        </Transition>

                        <Transition
                          show={open}
                          as={Fragment}
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items
                            static
                            className="origin-top-right z-40 absolute -right-2 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                          >
                            {userNavigation.map((item) => (
                              <Menu.Item key={item.name}>
                                {({ active }) => {
                                  if (item.to) {
                                    return (
                                      <Link
                                        to={item.to}
                                        className={cn(
                                          active ? "bg-gray-100" : "",
                                          "block px-4 py-2 text-sm text-gray-700"
                                        )}
                                      >
                                        {item.name}
                                      </Link>
                                    );
                                  } else if (item.signOut) {
                                    return (
                                      <button
                                        className="block px-4 py-2 text-sm text-gray-700"
                                        onClick={() => dispatch(logout())}
                                      >
                                        {item.name}
                                      </button>
                                    );
                                  }
                                  return null;
                                }}
                              </Menu.Item>
                            ))}
                          </Menu.Items>
                        </Transition>
                      </>
                    )}
                  </Menu>
                )}
              </div>

              {/* Left section */}
              <div className="w-full py-5 lg:border-t lg:border-white lg:border-opacity-20">
                <div className="lg:grid lg:grid-cols-3 lg:gap-8 lg:items-center">
                  {/* Left nav */}
                  <div className="hidden lg:block lg:col-span-2">
                    <nav className="flex space-x-4">
                      {navigation.map((item) => {
                        const match = matchPath(location.pathname, {
                          path: item.to,
                        });
                        return (
                          <NavLink
                            key={item.name}
                            to={item.to}
                            className={cn(
                              "text-sm font-medium rounded-md bg-white  px-3 py-2 hover:bg-opacity-10",
                              match
                                ? "text-white bg-opacity-10"
                                : "text-green-200 bg-opacity-0"
                            )}
                            aria-current={match ? "page" : undefined}
                          >
                            {item.name}
                          </NavLink>
                        );
                      })}
                    </nav>
                  </div>
                  {/* <div className="px-6 lg:px-0">
                    <div className="max-w-xs mx-auto w-full lg:max-w-md">
                      <label htmlFor="search" className="sr-only">
                        Search
                      </label>
                      <div className="relative text-white focus-within:text-gray-600">
                        <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center">
                          <SearchIcon className="h-5 w-5" aria-hidden="true" />
                        </div>
                        <input
                          id="search"
                          className="block w-full text-white bg-white bg-opacity-20 py-2 pl-10 pr-3 border border-transparent rounded-md leading-5 focus:text-gray-900 placeholder-white focus:outline-none focus:bg-opacity-100 focus:border-transparent focus:placeholder-gray-500 focus:ring-0 sm:text-sm"
                          placeholder="Search"
                          type="search"
                          name="search"
                        />
                      </div>
                    </div>
                  </div> */}
                </div>
              </div>

              {/* Menu button */}
              <div className="absolute right-0 flex-shrink-0 lg:hidden">
                <Popover.Button className="bg-transparent p-2 rounded-md inline-flex items-center justify-center text-cyan-200 hover:text-white hover:bg-white hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-white">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <MenuIcon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Popover.Button>
              </div>
            </div>
          </div>

          {/* Mobile nav menu */}
          <Transition.Root show={open} as={Fragment}>
            <div className="lg:hidden">
              <Transition.Child
                as={Fragment}
                enter="duration-150 ease-out"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="duration-150 ease-in"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Popover.Overlay
                  static
                  className="z-20 fixed inset-0 bg-black bg-opacity-25"
                />
              </Transition.Child>

              <Transition.Child
                as={Fragment}
                enter="duration-150 ease-out"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="duration-150 ease-in"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Popover.Panel
                  focus
                  static
                  className="z-30 absolute top-0 inset-x-0 max-w-3xl mx-auto w-full p-2 transition transform origin-top"
                >
                  <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-white divide-y divide-gray-200">
                    <div className="pt-3 pb-2">
                      <div className="flex items-center justify-between px-4">
                        <div>
                          <Logo
                            className="fill-current text-green-200"
                            type="plain"
                            size={30}
                          />
                        </div>
                        <div className="-mr-2">
                          <Popover.Button className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-500">
                            <span className="sr-only">Close menu</span>
                            <XIcon className="h-6 w-6" aria-hidden="true" />
                          </Popover.Button>
                        </div>
                      </div>
                      <div className="mt-3 px-2 space-y-1">
                        {navigation.map((item) => (
                          <Link
                            key={item.name}
                            to={item.to}
                            className="block rounded-md px-3 py-2 text-base text-gray-900 font-medium hover:bg-gray-100 hover:text-gray-800"
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                    <div className="pt-4 pb-2">
                      {data && (
                        <div className="flex items-center px-5">
                          <div className="min-w-0 flex-1">
                            <div className="text-lg font-medium text-gray-800 truncate">
                              {data.self.fullName}
                            </div>
                            <div className="text-sm font-medium text-gray-500 truncate">
                              {data.self.email}
                            </div>
                          </div>
                          <button className="ml-auto flex-shrink-0 bg-white p-1 text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500">
                            <span className="sr-only">View notifications</span>
                            <BellIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      )}
                      <div className="mt-3 px-2 space-y-1">
                        {userNavigation.map((item) => {
                          if (item.signOut) {
                            return (
                              <button
                                key={item.name}
                                className="block rounded-md px-3 py-2 text-base text-gray-900 font-medium hover:bg-gray-100 hover:text-gray-800"
                                onClick={() => dispatch(logout())}
                              >
                                {item.name}
                              </button>
                            );
                          } else if (item.to) {
                            return (
                              <Link
                                key={item.name}
                                to={item.to}
                                className="block rounded-md px-3 py-2 text-base text-gray-900 font-medium hover:bg-gray-100 hover:text-gray-800"
                              >
                                {item.name}
                              </Link>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  </div>
                </Popover.Panel>
              </Transition.Child>
            </div>
          </Transition.Root>
        </>
      )}
    </Popover>
  );
};

export default Header;
